import { useContext } from "react";
import type { backendReportActivityRowDTO } from "../../backend/target/typescript/dto";
import { AuthContext } from "../auth-context";
import { RequestContext } from "../request-context";
import { requestGet, requireResult } from "../utils/network";
import { asNumber } from "../utils/strings";
import { IPeriod } from "../widget/i-period";
import { Modes } from "../widget/io-abstract";
import IOList from "../widget/io-list";
import SearchPanel, { getPref, type FormValues } from "../widget/search-panel";
import TablePanel from "../widget/table-panel";

type ServerResponse = backendReportActivityRowDTO[];

function ageAtReference(
  year_of_birth: number | string | undefined,
  reference: string
): string {
  if (!year_of_birth) {
    return "-";
  }

  const year =
    typeof year_of_birth === "number"
      ? year_of_birth
      : Number.parseInt(year_of_birth.substring(0, 4), 10);

  const refYear = Number.parseInt((reference ?? "").substring(0, 4), 10);
  if (!Number.isFinite(refYear)) {
    return "?";
  }

  return "" + Math.max(0, refYear - year);
}

function oldOrNew(
  patientReference: string | undefined,
  reference: string
): string {
  const refYear = Number.parseInt((reference ?? "").substring(0, 4), 10);
  const patientYear = Number.parseInt(
    (patientReference ?? "").substring(0, 4),
    10
  );

  if (!Number.isFinite(refYear)) {
    return "?";
  }

  if (!Number.isFinite(patientYear)) {
    return "?";
  }

  if (patientYear < refYear) {
    return "Old(EN)";
  }

  return "New";
}

export default function ReportActivity(): React.ReactNode {
  const authenticationContext = useContext(AuthContext)!;
  const requestContext = useContext(RequestContext)!;

  const onSearch = (formData: FormValues): Promise<React.ReactNode> =>
    requestContext
      .fetchJSONAndMessage<ServerResponse>(
        "Generating report",
        requestGet("/api/reports/activity", formData)
      )
      .then(requireResult)
      .then((list) => (
        <TablePanel
          rows={list}
          headers={
            <thead>
              <tr>
                <th colSpan={23}>SARPV - AMD - KDM</th>
              </tr>
              <tr>
                <th colSpan={14}>Name of the project: Rickets in cox' Bazar</th>
                <th>When</th>
                <th colSpan={8}>
                  {formData["period"] === "day"
                    ? formData["day"]
                    : formData["period"] === "month"
                      ? formData["month"]
                      : formData["year"]}
                </th>
              </tr>
              <tr>
                <th colSpan={14}>
                  SARPV, Chakaria Disability Center, Chakaria, Cox's Bazar
                </th>
                <th>Where</th>
                <th colSpan={8}>{formData["center"]}</th>
              </tr>
              <tr>
                <th colSpan={14}>
                  With the great help of AMD and KDM - France
                </th>
                <th>Who</th>
                <th colSpan={8}>{formData["examiner"]}</th>
              </tr>
              <tr>
                <th colSpan={6}></th>
                <th colSpan={4}>Identity</th>
                <th colSpan={4}>SEL</th>
                <th>Medical</th>
                <th colSpan={8}>Price</th>
              </tr>
              <tr>
                <th></th>
                <th>N</th>
                <th>Date</th>
                <th>Examiner</th>
                <th>Center</th>
                <th>Entry Number</th>
                <th>Patient Name</th>
                <th>Age</th>
                <th>Sex</th>
                <th>Old/New</th>
                <th>Tk income</th>
                <th>Nb pers</th>
                <th>Tk per pers</th>
                <th>SL</th>
                <th>Pathology</th>
                <th>Consult</th>
                <th>Medicine</th>
                <th>Surgical</th>
                <th>Workshop</th>
                <th>Others</th>
                <th>Full</th>
                <th>Asked</th>
                <th>Paid</th>
              </tr>
            </thead>
          }
          lineLink={(row) => `/patient/${row.patient_id}/bill/${row.bill_id}`}
          lineColumns={(row, index) => {
            const complementary = !!row.complementary;
            const consult = complementary ? 0 : asNumber(row.total_consult);
            const medecine = complementary ? 0 : asNumber(row.total_medecine);
            const surgical = complementary ? 0 : asNumber(row.total_surgical);
            const workshop = complementary ? 0 : asNumber(row.total_workshop);
            const other = complementary ? 0 : asNumber(row.total_other);
            const real = complementary ? 0 : asNumber(row.total_real);
            const asked = complementary ? 0 : asNumber(row.total_asked);
            const paid = asNumber(row.total_paid);

            const household = asNumber(
              row.social_level_number_of_household_members
            );
            const salary = asNumber(row.social_level_family_salary);

            return (
              <>
                <td>{index + 1}</td>
                <td>{row.bill_date ?? ""}</td>
                <td>{row.examiner ?? ""}</td>
                <td>{row.center ?? ""}</td>
                <td>{row.reference ?? ""}</td>
                <td>{row.name ?? ""}</td>
                <td>
                  {ageAtReference(row.year_of_birth, formData["date_start"])}
                </td>
                <td>{row.sex ?? ""}</td>
                <td>{oldOrNew(row.reference, formData["date_start"])}</td>
                <td>{salary || ""}</td>
                <td>{household || ""}</td>
                <td>{household ? Math.round(salary / household) : ""}</td>
                <td>{row.social_level ?? ""}</td>
                <td>{row.pathology ?? ""}</td>
                {complementary ? (
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    Complementary payments{" "}
                  </td>
                ) : (
                  <>
                    <td>{consult || ""}</td>
                    <td>{medecine || ""}</td>
                    <td>{surgical || ""}</td>
                    <td>{workshop || ""}</td>
                    <td>{other || ""}</td>
                    <td>{real || ""}</td>
                    <td>{asked || ""}</td>
                  </>
                )}
                <td>{paid || ""}</td>
              </>
            );
          }}
          footers={
            <tfoot>
              <tr>
                <th colSpan={15}>total</th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_consult),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_medecine),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_surgical),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_workshop),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_other),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_real),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_asked),
                    0
                  )}
                </th>
                <th>
                  {list?.reduce(
                    (acc, row) => acc + asNumber(row.total_paid),
                    0
                  )}
                </th>
              </tr>
            </tfoot>
          }
          reportBasename={`activity ${formData["period"] == "day" ? formData["day"] : formData["period"] == "month" ? formData["month"] : formData["year"]}`}
        />
      ));

  return (
    <div>
      <h1>
        <img src="/static/img/reports.gif" />
        Activity Report
      </h1>

      <SearchPanel
        form={
          <>
            <IPeriod />
            <IOList
              name="center"
              mode={Modes.input}
              list={authenticationContext.getList("Centers")}
              value={getPref("center", "")}
            />
            <IOList
              name="examiner"
              mode={Modes.input}
              list={authenticationContext.getList("Examiners")}
              value={getPref("examiner", "")}
            />
            <IOList
              name="activity"
              mode={Modes.input}
              list={["consult", "workshop", "surgical"]}
              value={getPref("activity", "")}
            />
          </>
        }
        onSearch={onSearch}
      />
    </div>
  );
}
